import { Component, Inject, inject, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatStepperModule } from '@angular/material/stepper';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../../shared/state.service';
import { ErrorService } from '../../shared/error.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { UtilityService } from '../../shared/utility.service';
import { Task, ServiceRequest, MedicationRequest, Observation, DiagnosticReport, Procedure, ImagingStudy, MedicationAdministration, MedicationDispense } from 'fhir/r4';
import { firstValueFrom } from 'rxjs';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { ReferenceDisplayDirective } from '../../shared/reference-display.directive';
import { ChipsDirective } from '../../chips.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { inferServiceRequestType, type ServiceRequestType } from '../../shared/service-request-type.util';

interface FulfillTaskData {
  task: Task;
  purpose?: 'fulfill' | 'complete' | 'review';
  patientId?: string;
}

@Component({
  selector: 'app-fulfill-task',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatStepperModule,
    MatDividerModule,
    MatCheckboxModule,
    MatRadioModule,
    ReactiveFormsModule,
    ReferenceDisplayDirective,
    ChipsDirective,
    DynamicFormsV2Component,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './fulfill-task.component.html',
  styleUrl: './fulfill-task.component.scss'
})
export class FulfillTaskComponent implements OnInit {
  task!: Task;
  purpose: 'fulfill' | 'complete' | 'review' = 'fulfill';
  patientId?: string;

  // Task classification
  taskType: 'vitals' | 'service-request' | 'medication' | 'observation' | 'lab' | 'generic' = 'generic';

  // Related resources
  relatedServiceRequest?: any;
  relatedMedicationRequest?: any;
  completedServiceRequests: any[] = [];
  completedMedicationRequests: any[] = [];
  completionValidationMessages: string[] = [];
  completionEvidenceMessages: string[] = [];

  // Forms
  fulfillmentForm!: FormGroup;
  completionForm!: FormGroup;

  // UI state
  loading = false;
  showCompletionStep = false;
  canMarkComplete = false;

  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private stateService = inject(StateService);
  private errorService = inject(ErrorService);
  private notificationService = inject(NotificationService);
  private utilityService = inject(UtilityService);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: FulfillTaskData,
    @Optional() public dialogRef?: MatDialogRef<FulfillTaskComponent>
  ) { }

  async ngOnInit() {
    // Handle both dialog mode and standalone route mode
    if (!this.data?.task) {
      // Standalone mode: generate sample task for testing
      await this.loadSampleTask();
    } else {
      // Dialog mode: use provided task data
      this.task = this.toFhirTaskResource(this.data.task);
      this.purpose = this.data.purpose || 'fulfill';
      this.patientId = this.data.patientId || this.extractPatientId(this.task);
    }

    // Initialize forms
    this.initializeForms();

    // Classify task and fetch related resources
    await this.classifyTask();
    await this.fetchRelatedResources();

    // Determine if user can mark task as complete
    await this.evaluateCompletionEligibility();
  }

  /**
   * Load sample task data for testing in standalone mode
   */
  private async loadSampleTask(): Promise<void> {
    // Generate sample task for testing
    this.task = {
      resourceType: 'Task',
      id: 'sample-task-123',
      status: 'requested',
      intent: 'order',
      code: {
        coding: [{
          system: 'http://hl7.org/fhir/CodeSystem/task-code',
          code: 'fulfill',
          display: 'Fulfill the focal request'
        }]
      },
      description: 'Record patient vitals - Blood Pressure, Temperature, Heart Rate',
      for: {
        reference: 'Patient/sample-patient-456',
        display: 'John Doe'
      },
      restriction: {
        period: {
          start: new Date().toISOString(),
          end: new Date(Date.now() + 3600000).toISOString() // 1 hour from now
        }
      },
      authoredOn: new Date().toISOString(),
      priority: 'routine'
    };

    this.purpose = 'fulfill';
    this.patientId = 'sample-patient-456';

    console.log('Loaded sample task for testing:', this.task);
  }

  /**
   * Initialize reactive forms for fulfillment and completion
   */
  private initializeForms() {
    this.fulfillmentForm = this.fb.group({
      fulfillmentType: ['', Validators.required],
      notes: [''],
      completedDateTime: [new Date().toISOString()]
    });

    this.completionForm = this.fb.group({
      completionNotes: [''],
      outcome: ['completed', Validators.required],
      selectedServiceRequests: [[]],
      selectedMedicationRequests: [[]]
    });
  }

  /**
   * Classify task based on focus, code, and description
   */
  private async classifyTask() {
    const focusRef = this.task.focus?.reference;
    const description = (this.task.description || '').toLowerCase();
    const codeText = (this.task.code?.text || '').toLowerCase();

    // Check focus reference type
    if (focusRef) {
      if (focusRef.startsWith('ServiceRequest/')) {
        this.taskType = 'service-request';
      } else if (focusRef.startsWith('MedicationRequest/')) {
        this.taskType = 'medication';
      }
    }

    // Check description/code for vital signs indicators
    const vitalKeywords = ['vital', 'blood pressure', 'temperature', 'pulse', 'respiration', 'spo2', 'oxygen'];
    if (vitalKeywords.some(keyword => description.includes(keyword) || codeText.includes(keyword))) {
      this.taskType = 'vitals';
    }

    // Check for observation/lab keywords
    const labKeywords = ['lab', 'test', 'specimen', 'observation'];
    if (labKeywords.some(keyword => description.includes(keyword) || codeText.includes(keyword))) {
      this.taskType = 'lab';
    }

    console.log('Task classified as:', this.taskType);
  }

  /**
   * Fetch related FHIR resources (ServiceRequest, MedicationRequest, etc.)
   */
  private async fetchRelatedResources() {
    const focusRef = this.task.focus?.reference;
    if (!focusRef) return;

    try {
      const [resourceType, resourceId] = focusRef.split('/');
      const url = `${this.backendUrl}/${resourceType}/${resourceId}`;
      const resource = await firstValueFrom(this.http.get(url));

      if (resourceType === 'ServiceRequest') {
        this.relatedServiceRequest = resource;
      } else if (resourceType === 'MedicationRequest') {
        this.relatedMedicationRequest = resource;
      }

      console.log('Fetched related resource:', resource);
    } catch (error) {
      console.warn('Could not fetch related resource:', error);
    }
  }

  /**
   * Evaluate if task can be marked complete based on linked resources
   */
  private async evaluateCompletionEligibility() {
    if (this.purpose === 'complete') {
      this.showCompletionStep = true;
    }

    this.completionValidationMessages = [];
    this.completionEvidenceMessages = [];

    const focusRef = this.task.focus?.reference || '';
    if (focusRef.startsWith('ServiceRequest/') && this.relatedServiceRequest) {
      await this.evaluateServiceRequestCompletion(this.relatedServiceRequest as ServiceRequest);
      return;
    }

    if (focusRef.startsWith('MedicationRequest/') && this.relatedMedicationRequest) {
      await this.evaluateMedicationRequestCompletion(this.relatedMedicationRequest as MedicationRequest);
      return;
    }

    // Fetch completed service requests and medication requests for this patient
    if (this.patientId) {
      await this.fetchCompletedResources();
    }

    // Check if task has completed dependencies
    const hasCompletedWork =
      this.completedServiceRequests.length > 0 ||
      this.completedMedicationRequests.length > 0;

    this.canMarkComplete = hasCompletedWork || this.taskType === 'vitals';

    if (!this.canMarkComplete) {
      this.completionValidationMessages = [
        'No linked completion evidence was found for this task yet.'
      ];
    }
  }

  private async evaluateServiceRequestCompletion(serviceRequest: ServiceRequest): Promise<void> {
    const serviceRequestId = (serviceRequest?.id ?? '').toString().trim();
    if (!serviceRequestId) {
      this.canMarkComplete = false;
      this.completionValidationMessages = ['The linked ServiceRequest is missing an id.'];
      return;
    }

    const serviceRequestRef = `ServiceRequest/${serviceRequestId}`;
    const serviceRequestType = inferServiceRequestType(serviceRequest);
    const isObservationRequest = this.isObservationServiceRequest(serviceRequest);

    switch (serviceRequestType) {
      case 'procedure':
      case 'surgery': {
        const procedures = await this.fetchProcedureEvidence(serviceRequestRef);
        const completedProcedures = procedures.filter((procedure) => this.isProcedureCompleted(procedure));

        this.canMarkComplete = completedProcedures.length > 0;
        if (this.canMarkComplete) {
          this.completionEvidenceMessages = [`Found ${completedProcedures.length} completed Procedure resource(s) based on this request.`];
        } else {
          this.completionValidationMessages = ['A completed Procedure based on this ServiceRequest is required before marking the task complete.'];
        }
        return;
      }

      case 'others': {
        const observations = await this.fetchObservationEvidence(serviceRequestRef);
        const finalObservations = observations.filter((observation) => this.isObservationFinal(observation));

        if (isObservationRequest) {
          this.canMarkComplete = finalObservations.length > 0;
          if (this.canMarkComplete) {
            this.completionEvidenceMessages = [`Found ${finalObservations.length} final Observation result(s) based on this request.`];
          } else {
            this.completionValidationMessages = ['A final Observation based on this ServiceRequest is required before marking the task complete.'];
          }
          return;
        }

        const reports = await this.fetchDiagnosticReportEvidence(serviceRequestRef);
        const finalReports = reports.filter((report) => this.isDiagnosticReportFinal(report));
        const procedures = await this.fetchProcedureEvidence(serviceRequestRef);
        const completedProcedures = procedures.filter((procedure) => this.isProcedureCompleted(procedure));

        this.canMarkComplete = finalObservations.length > 0 || finalReports.length > 0 || completedProcedures.length > 0;
        if (this.canMarkComplete) {
          this.completionEvidenceMessages = [
            ...(finalObservations.length ? [`Found ${finalObservations.length} final Observation result(s).`] : []),
            ...(finalReports.length ? [`Found ${finalReports.length} final DiagnosticReport resource(s).`] : []),
            ...(completedProcedures.length ? [`Found ${completedProcedures.length} completed Procedure resource(s).`] : [])
          ];
        } else {
          this.completionValidationMessages = ['Complete this task by recording a final Observation, a final DiagnosticReport, or a completed Procedure based on the linked ServiceRequest.'];
        }
        return;
      }

      case 'laboratory': {
        const reports = await this.fetchDiagnosticReportEvidence(serviceRequestRef);
        const finalReports = reports.filter((report) => this.isDiagnosticReportFinal(report));
        const observations = await this.fetchObservationEvidence(serviceRequestRef);
        const finalObservations = observations.filter((observation) => this.isObservationFinal(observation));

        this.canMarkComplete = finalReports.length > 0;
        if (this.canMarkComplete) {
          this.completionEvidenceMessages = [`Found ${finalReports.length} final DiagnosticReport resource(s) based on this laboratory request.`];
        } else {
          this.completionValidationMessages = ['A final DiagnosticReport based on this laboratory ServiceRequest is required before marking the task complete.'];
          if (finalObservations.length > 0) {
            this.completionEvidenceMessages = ['Final Observation results exist, but this workflow still requires a final DiagnosticReport for laboratory completion.'];
          }
        }
        return;
      }

      case 'imaging': {
        const reports = await this.fetchDiagnosticReportEvidence(serviceRequestRef);
        const finalReports = reports.filter((report) => this.isDiagnosticReportFinal(report));
        const imagingStudies = await this.fetchImagingStudyEvidence(serviceRequestRef);

        this.canMarkComplete = finalReports.length > 0;
        if (this.canMarkComplete) {
          this.completionEvidenceMessages = [`Found ${finalReports.length} final DiagnosticReport resource(s) based on this imaging request.`];
        } else {
          this.completionValidationMessages = ['A final DiagnosticReport based on this imaging ServiceRequest is required before marking the task complete.'];
          if (imagingStudies.length > 0) {
            this.completionEvidenceMessages = ['ImagingStudy records exist, but this workflow still requires a final DiagnosticReport for imaging completion.'];
          }
        }
        return;
      }
    }
  }

  private isObservationServiceRequest(serviceRequest: ServiceRequest | null | undefined): boolean {
    const categories = Array.isArray(serviceRequest?.category) ? serviceRequest.category : [];
    const values = categories.flatMap((category: any) => [
      String(category?.text ?? '').trim().toLowerCase(),
      ...(Array.isArray(category?.coding)
        ? category.coding.flatMap((coding: any) => [
          String(coding?.code ?? '').trim().toLowerCase(),
          String(coding?.display ?? '').trim().toLowerCase()
        ])
        : [])
    ]).filter(Boolean);

    return values.includes('observation');
  }

  private async evaluateMedicationRequestCompletion(medicationRequest: MedicationRequest): Promise<void> {
    const medicationRequestId = (medicationRequest?.id ?? '').toString().trim();
    if (!medicationRequestId) {
      this.canMarkComplete = false;
      this.completionValidationMessages = ['The linked MedicationRequest is missing an id.'];
      return;
    }

    const medicationRequestRef = `MedicationRequest/${medicationRequestId}`;
    const administrations = await this.fetchMedicationAdministrationEvidence(medicationRequestRef);
    const completedAdministrations = administrations.filter((administration) => this.isMedicationAdministrationCompleted(administration));
    const dispenses = await this.fetchMedicationDispenseEvidence(medicationRequestRef);
    const completedDispenses = dispenses.filter((dispense) => this.isMedicationDispenseCompleted(dispense));

    this.canMarkComplete = completedAdministrations.length > 0 || completedDispenses.length > 0;
    if (this.canMarkComplete) {
      this.completionEvidenceMessages = [
        ...(completedAdministrations.length ? [`Found ${completedAdministrations.length} completed MedicationAdministration resource(s).`] : []),
        ...(completedDispenses.length ? [`Found ${completedDispenses.length} completed MedicationDispense resource(s).`] : [])
      ];
    } else {
      this.completionValidationMessages = ['A completed MedicationAdministration or MedicationDispense linked to this MedicationRequest is required before marking the task complete.'];
    }
  }

  /**
   * Fetch completed ServiceRequests and MedicationRequests for this patient
   */
  private async fetchCompletedResources() {
    try {
      const [srBundle, mrBundle] = await Promise.all([
        firstValueFrom(this.http.get(`${this.backendUrl}/ServiceRequest?patient=${this.patientId}&status=completed&_sort=-_lastUpdated&_count=50`)),
        firstValueFrom(this.http.get(`${this.backendUrl}/MedicationRequest?patient=${this.patientId}&status=completed&_sort=-_lastUpdated&_count=50`))
      ]);

      this.completedServiceRequests = (srBundle as any)?.entry?.map((e: any) => e.resource) || [];
      this.completedMedicationRequests = (mrBundle as any)?.entry?.map((e: any) => e.resource) || [];

      console.log('Completed resources:', {
        serviceRequests: this.completedServiceRequests.length,
        medicationRequests: this.completedMedicationRequests.length
      });
    } catch (error) {
      console.error('Error fetching completed resources:', error);
    }
  }

  /**
   * Extract patient ID from task
   */
  private extractPatientId(task: Task): string | undefined {
    const forRef = task.for?.reference;
    if (forRef?.startsWith('Patient/')) {
      return forRef.split('/')[1];
    }
    return undefined;
  }

  /**
   * Open appropriate fulfillment form based on task type
   */
  async openFulfillmentForm() {
    if (await this.openFocusedResourceWorkflow()) {
      return;
    }

    switch (this.taskType) {
      case 'vitals':
        this.openVitalsForm();
        break;
      case 'service-request':
        this.openServiceRequestForm();
        break;
      case 'medication':
        this.openMedicationAdministrationForm();
        break;
      case 'lab':
        this.openLabResultsForm();
        break;
      default:
        this.openGenericFulfillmentForm();
    }
  }

  private async openFocusedResourceWorkflow(): Promise<boolean> {
    const focusRef = this.task.focus?.reference || '';

    if (focusRef.startsWith('ServiceRequest/') && this.relatedServiceRequest?.id) {
      const serviceRequest = this.relatedServiceRequest as ServiceRequest;
      const patientId = this.extractPatientId(this.task)
        || this.extractPatientIdFromReference(serviceRequest.subject?.reference)
        || this.patientId
        || undefined;
      const inferredType = inferServiceRequestType(serviceRequest);
      const routeType = this.mapServiceRequestTypeToRouteSegment(inferredType);
      const commands = patientId
        ? ['/app', 'patients', patientId, 'service-requests', routeType]
        : ['/app', 'service-requests', routeType];

      await this.router.navigate(commands, {
        queryParams: { serviceRequestId: serviceRequest.id }
      });
      this.close({ redirected: true, focus: 'ServiceRequest' });
      return true;
    }

    if (focusRef.startsWith('MedicationRequest/') && this.relatedMedicationRequest?.id) {
      const medicationRequest = this.relatedMedicationRequest as MedicationRequest;
      const patientId = this.extractPatientId(this.task)
        || this.extractPatientIdFromReference(medicationRequest.subject?.reference)
        || this.patientId
        || undefined;
      const commands = patientId
        ? ['/app', 'patients', patientId, 'medications']
        : ['/app', 'medicine-requests'];

      await this.router.navigate(commands, {
        queryParams: { medRequestId: medicationRequest.id }
      });
      this.close({ redirected: true, focus: 'MedicationRequest' });
      return true;
    }

    return false;
  }

  private mapServiceRequestTypeToRouteSegment(type: ServiceRequestType): string {
    if (type === 'laboratory') {
      return 'lab-tests';
    }
    return type;
  }

  private extractPatientIdFromReference(reference: string | null | undefined): string | undefined {
    const raw = (reference ?? '').toString().trim();
    if (!raw.startsWith('Patient/')) {
      return undefined;
    }
    return raw.split('/')[1] || undefined;
  }

  private async fetchObservationEvidence(serviceRequestRef: string): Promise<Observation[]> {
    return this.fetchBundleResources<Observation>(`${this.backendUrl}/Observation?based-on=${encodeURIComponent(serviceRequestRef)}&_count=100`);
  }

  private async fetchDiagnosticReportEvidence(serviceRequestRef: string): Promise<DiagnosticReport[]> {
    return this.fetchBundleResources<DiagnosticReport>(`${this.backendUrl}/DiagnosticReport?based-on=${encodeURIComponent(serviceRequestRef)}&_count=100`);
  }

  private async fetchProcedureEvidence(serviceRequestRef: string): Promise<Procedure[]> {
    return this.fetchBundleResources<Procedure>(`${this.backendUrl}/Procedure?based-on=${encodeURIComponent(serviceRequestRef)}&_count=100`);
  }

  private async fetchImagingStudyEvidence(serviceRequestRef: string): Promise<ImagingStudy[]> {
    return this.fetchBundleResources<ImagingStudy>(`${this.backendUrl}/ImagingStudy?basedon=${encodeURIComponent(serviceRequestRef)}&_count=100`);
  }

  private async fetchMedicationAdministrationEvidence(medicationRequestRef: string): Promise<MedicationAdministration[]> {
    return this.fetchBundleResources<MedicationAdministration>(`${this.backendUrl}/MedicationAdministration?request=${encodeURIComponent(medicationRequestRef)}&_count=100`);
  }

  private async fetchMedicationDispenseEvidence(medicationRequestRef: string): Promise<MedicationDispense[]> {
    return this.fetchBundleResources<MedicationDispense>(`${this.backendUrl}/MedicationDispense?prescription=${encodeURIComponent(medicationRequestRef)}&_count=100`);
  }

  private async fetchBundleResources<T>(url: string): Promise<T[]> {
    try {
      const bundle = await firstValueFrom(this.http.get<any>(url));
      return (bundle?.entry ?? [])
        .filter((entry: any) => entry?.resource && entry?.resource?.resourceType !== 'OperationOutcome' && entry?.search?.mode !== 'outcome')
        .map((entry: any) => entry.resource as T);
    } catch (error) {
      console.error('Failed to fetch task completion evidence from', url, error);
      return [];
    }
  }

  private isObservationFinal(observation: Observation | null | undefined): boolean {
    const status = (observation?.status ?? '').toString().trim().toLowerCase();
    return ['final', 'amended', 'corrected'].includes(status);
  }

  private isDiagnosticReportFinal(report: DiagnosticReport | null | undefined): boolean {
    const status = (report?.status ?? '').toString().trim().toLowerCase();
    return ['final', 'amended', 'corrected', 'appended'].includes(status);
  }

  private isProcedureCompleted(procedure: Procedure | null | undefined): boolean {
    return (procedure?.status ?? '').toString().trim().toLowerCase() === 'completed';
  }

  private isMedicationAdministrationCompleted(administration: MedicationAdministration | null | undefined): boolean {
    return (administration?.status ?? '').toString().trim().toLowerCase() === 'completed';
  }

  private isMedicationDispenseCompleted(dispense: MedicationDispense | null | undefined): boolean {
    return (dispense?.status ?? '').toString().trim().toLowerCase() === 'completed';
  }

  /**
   * Open vitals recording form
   */
  private openVitalsForm() {
    // TODO: Implement VitalsComponent and uncomment
    // const dialogRef = this.dialog.open(VitalsComponent, {
    //   width: '90vw',
    //   maxWidth: '1200px',
    //   data: {
    //     patientId: this.patientId,
    //     taskId: this.task.id,
    //     returnAfterSave: true
    //   }
    // });

    // dialogRef.afterClosed().subscribe(result => {
    //   if (result?.saved) {
    //     this.notificationService.success('Vitals recorded successfully');
    //     this.canMarkComplete = true;
    //     this.showCompletionStep = true;
    //   }
    // });

    // Temporary placeholder: use generic form
    this.notificationService.info('Vitals form not yet implemented. Using generic fulfillment form.');
    this.openGenericFulfillmentForm();
  }

  /**
   * Open service request fulfillment form
   */
  private openServiceRequestForm() {
    // Use EncounterServiceService or create a dedicated service request component
    this.notificationService.info('Opening service request form...');
    // TODO: Implement service request fulfillment UI
  }

  /**
   * Open medication administration form
   */
  private openMedicationAdministrationForm() {
    this.notificationService.info('Opening medication administration form...');
    // TODO: Implement medication administration UI
  }

  /**
   * Open lab results recording form
   */
  private openLabResultsForm() {
    this.notificationService.info('Opening lab results form...');
    // TODO: Implement lab results recording UI
  }

  /**
   * Open generic task fulfillment form using DynamicFormsV2
   */
  private openGenericFulfillmentForm() {
    // Generic note-taking form for tasks without specific fulfillment workflow
    const dialogRef = this.dialog.open(DynamicFormsV2Component, {
      width: '600px',
      data: {
        formFields: [{
          generalProperties: {
            fieldApiName: 'fulfillmentNotes',
            fieldName: 'Fulfillment Notes',
            fieldLabel: 'Record task fulfillment details',
            fieldType: 'IndividualField',
            inputType: 'textarea',
            isArray: false,
            isGroup: false,
            validations: [{ type: 'default', name: 'required', isFunction: false }]
          }
        }],
        formMetaData: {
          formName: 'Task Fulfillment',
          formDescription: 'Record details about task completion',
          closeDialogOnSubmit: true
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.values) {
        this.fulfillmentForm.patchValue({
          notes: result.values.fulfillmentNotes
        });
        this.canMarkComplete = true;
        this.showCompletionStep = true;
      }
    });
  }

  /**
   * Mark task as in-progress
   */
  async markInProgress() {
    await this.updateTaskStatus('in-progress');
  }

  /**
   * Mark task as completed
   */
  async markComplete() {
    if (!this.canMarkComplete) {
      this.errorService.openandCloseError('Please fulfill the task requirements before marking as complete');
      return;
    }

    const outcome = this.completionForm.get('outcome')?.value || 'completed';
    const notes = this.completionForm.get('completionNotes')?.value;

    // Build Task update
    const updatedTask: any = {
      ...this.toFhirTaskResource(this.task),
      status: outcome,
      lastModified: new Date().toISOString()
    };

    // Add output notes if provided
    if (notes) {
      updatedTask.output = updatedTask.output || [];
      updatedTask.output.push({
        type: {
          text: 'Completion Notes'
        },
        valueString: notes
      });
    }

    // Link completed service requests/medications
    const selectedSRs = this.completionForm.get('selectedServiceRequests')?.value || [];
    const selectedMRs = this.completionForm.get('selectedMedicationRequests')?.value || [];

    if (selectedSRs.length > 0 || selectedMRs.length > 0) {
      updatedTask.output = updatedTask.output || [];
      updatedTask.output.push({
        type: { text: 'Completed Work References' },
        valueReference: {
          reference: [...selectedSRs, ...selectedMRs].join(', ')
        }
      });
    }

    await this.submitTaskUpdate(updatedTask);
  }

  /**
   * Update task status
   */
  private async updateTaskStatus(status: string) {
    const updatedTask = {
      ...this.toFhirTaskResource(this.task),
      status,
      lastModified: new Date().toISOString()
    };

    await this.submitTaskUpdate(updatedTask);
  }

  /**
   * Submit task update to backend
   */
  private async submitTaskUpdate(task: any) {
    this.loading = true;

    try {
      const url = `${this.backendUrl}/Task/${task.id}`;
      const result = await firstValueFrom(this.http.put(url, task));

      console.log('Task updated:', result);
      this.stateService.processBundleTransaction({ entry: [{ resource: result }] } as any);

      this.notificationService.success(`Task ${task.status}`);
      this.close(result);
    } catch (error: any) {
      console.error('Task update error:', error);
      this.errorService.openandCloseError(
        `Failed to update task: ${error.error?.issue?.[0]?.diagnostics || error.message || 'Unknown error'}`
      );
    } finally {
      this.loading = false;
    }
  }

  private toFhirTaskResource(task: any): Task {
    const allowedKeys = new Set([
      'resourceType',
      'id',
      'meta',
      'implicitRules',
      'language',
      'text',
      'contained',
      'extension',
      'modifierExtension',
      'identifier',
      'instantiatesCanonical',
      'instantiatesUri',
      'basedOn',
      'groupIdentifier',
      'partOf',
      'status',
      'statusReason',
      'businessStatus',
      'intent',
      'priority',
      'code',
      'description',
      'focus',
      'for',
      'encounter',
      'executionPeriod',
      'authoredOn',
      'lastModified',
      'requester',
      'performerType',
      'owner',
      'location',
      'reasonCode',
      'reasonReference',
      'insurance',
      'note',
      'relevantHistory',
      'restriction',
      'input',
      'output'
    ]);

    const sanitized = Object.entries(task ?? {}).reduce((result, [key, value]) => {
      if (allowedKeys.has(key) && value !== undefined) {
        result[key] = value;
      }
      return result;
    }, {} as Record<string, any>);

    return {
      resourceType: 'Task',
      ...sanitized
    } as Task;
  }

  /**
   * Close component (navigate back in standalone mode, close dialog in dialog mode)
   */
  close(result?: any) {
    if (this.dialogRef) {
      // Dialog mode
      this.dialogRef.close(result);
    } else {
      // Standalone mode - navigate back
      window.history.back();
    }
  }

  /**
   * Get display name for task type
   */
  getTaskTypeDisplay(): string {
    const typeMap: Record<typeof this.taskType, string> = {
      'vitals': 'Vital Signs Recording',
      'service-request': 'Service Request Fulfillment',
      'medication': 'Medication Administration',
      'lab': 'Lab Results Recording',
      'observation': 'Clinical Observation',
      'generic': 'Task Fulfillment'
    };
    return typeMap[this.taskType];
  }

  /**
   * Get appropriate icon for task type
   */
  getTaskTypeIcon(): string {
    const iconMap: Record<typeof this.taskType, string> = {
      'vitals': 'favorite',
      'service-request': 'assignment',
      'medication': 'medication',
      'lab': 'science',
      'observation': 'visibility',
      'generic': 'task_alt'
    };
    return iconMap[this.taskType];
  }
}
